export type TimerMode = "countdown" | "stopwatch";

export type DurationParts = {
  hours: number;
  minutes: number;
  seconds: number;
};

export type WorldClockCity = {
  id: string;
  label: string;
  latitude: number;
  longitude: number;
};

export const TIMER_WORLD_CLOCK: WorldClockCity[] = [
  { id: "Asia/Bangkok", label: "Bangkok", latitude: 13.7563, longitude: 100.5018 },
  { id: "Asia/Dubai", label: "Dubai", latitude: 25.2048, longitude: 55.2708 },
  {
    id: "Europe/Helsinki",
    label: "Helsinki",
    latitude: 60.1699,
    longitude: 24.9384,
  },
  { id: "Europe/Madrid", label: "Madrid", latitude: 40.4168, longitude: -3.7038 },
  {
    id: "America/Los_Angeles",
    label: "Los Angeles",
    latitude: 34.0522,
    longitude: -118.2437,
  },
  {
    id: "America/New_York",
    label: "New York",
    latitude: 40.7128,
    longitude: -74.006,
  },
  { id: "Asia/Tokyo", label: "Tokyo", latitude: 35.6762, longitude: 139.6503 },
  {
    id: "Europe/London",
    label: "United Kingdom",
    latitude: 51.5074,
    longitude: -0.1278,
  },
  {
    id: "Asia/Shanghai",
    label: "Shanghai",
    latitude: 31.2304,
    longitude: 121.4737,
  },
  {
    id: "Asia/Singapore",
    label: "Singapore",
    latitude: 1.3521,
    longitude: 103.8198,
  },
  {
    id: "Australia/Sydney",
    label: "Sydney",
    latitude: -33.8688,
    longitude: 151.2093,
  },
];

export const WORLD_CLOCK_PREFS_KEY = "trifecta-world-clock-visible";

/** Cities shown on first visit. */
export const DEFAULT_WORLD_CLOCK_VISIBLE_IDS = [
  "Asia/Bangkok",
  "Asia/Dubai",
  "Europe/Helsinki",
  "Europe/Madrid",
  "America/Los_Angeles",
  "America/New_York",
  "Asia/Tokyo",
  "Europe/London",
  "Asia/Shanghai",
  "Asia/Singapore",
] as const;

type WorldClockPrefs = { v: 2; ids: string[] };

const VALID_WORLD_CLOCK_IDS = new Set(TIMER_WORLD_CLOCK.map((city) => city.id));

function normalizeWorldClockIds(ids: unknown[]): string[] {
  return ids.filter(
    (id): id is string =>
      typeof id === "string" && VALID_WORLD_CLOCK_IDS.has(id),
  );
}

function orderWorldClockIds(ids: string[]): string[] {
  const selected = new Set(ids);
  return TIMER_WORLD_CLOCK.filter((city) => selected.has(city.id)).map(
    (city) => city.id,
  );
}

function mergeWithDefaultIds(ids: string[]): string[] {
  const selected = new Set([...ids, ...DEFAULT_WORLD_CLOCK_VISIBLE_IDS]);
  return orderWorldClockIds([...selected]);
}

export function defaultWorldClockVisibleIds(): string[] {
  return [...DEFAULT_WORLD_CLOCK_VISIBLE_IDS];
}

export function loadWorldClockVisibleIds(): string[] {
  try {
    const raw = localStorage.getItem(WORLD_CLOCK_PREFS_KEY);
    if (!raw) return defaultWorldClockVisibleIds();

    const parsed = JSON.parse(raw) as unknown;

    if (Array.isArray(parsed)) {
      const ids = normalizeWorldClockIds(parsed);
      const migrated = mergeWithDefaultIds(
        ids.length > 0 ? ids : defaultWorldClockVisibleIds(),
      );
      saveWorldClockVisibleIds(migrated);
      return migrated;
    }

    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "ids" in parsed &&
      Array.isArray((parsed as WorldClockPrefs).ids)
    ) {
      const ids = normalizeWorldClockIds((parsed as WorldClockPrefs).ids);
      return ids.length > 0 ? orderWorldClockIds(ids) : defaultWorldClockVisibleIds();
    }

    return defaultWorldClockVisibleIds();
  } catch {
    return defaultWorldClockVisibleIds();
  }
}

export function saveWorldClockVisibleIds(ids: string[]): void {
  const normalized = orderWorldClockIds(normalizeWorldClockIds(ids));
  if (normalized.length === 0) return;

  const payload: WorldClockPrefs = { v: 2, ids: normalized };
  localStorage.setItem(WORLD_CLOCK_PREFS_KEY, JSON.stringify(payload));
}

export function visibleWorldClockCities(ids: string[]): WorldClockCity[] {
  const selected = new Set(ids);
  return TIMER_WORLD_CLOCK.filter((city) => selected.has(city.id));
}

export function toggleWorldClockCity(
  ids: string[],
  cityId: string,
): string[] | null {
  const selected = new Set(ids);
  if (selected.has(cityId)) {
    if (selected.size <= 1) return null;
    selected.delete(cityId);
  } else {
    selected.add(cityId);
  }

  return orderWorldClockIds([...selected]);
}

export function clampPart(value: number, max: number): number {
  if (!Number.isFinite(value) || value < 0) return 0;
  return Math.min(Math.floor(value), max);
}

export function durationFromParts(parts: DurationParts): number {
  const hours = clampPart(parts.hours, 99);
  const minutes = clampPart(parts.minutes, 59);
  const seconds = clampPart(parts.seconds, 59);
  return ((hours * 60 + minutes) * 60 + seconds) * 1000;
}

export function partsFromDuration(ms: number): DurationParts {
  const total = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return { hours, minutes, seconds };
}

export function formatDuration(
  ms: number,
  options?: { forceHours?: boolean; centiseconds?: boolean },
): string {
  const negative = ms < 0;
  const abs = Math.abs(ms);
  const totalCs = Math.floor(abs / 10);
  const cs = totalCs % 100;
  const totalSec = Math.floor(abs / 1000);
  const hours = Math.floor(totalSec / 3600);
  const minutes = Math.floor((totalSec % 3600) / 60);
  const seconds = totalSec % 60;

  const showHours = options?.forceHours || hours > 0;
  const pad = (n: number) => String(n).padStart(2, "0");

  let body: string;
  if (showHours) {
    body = `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  } else {
    body = `${pad(minutes)}:${pad(seconds)}`;
  }

  if (options?.centiseconds) {
    body += `.${pad(cs)}`;
  }

  return negative ? `-${body}` : body;
}

export function formatWorldTime(
  timeZone: string,
  now = new Date(),
  hour12 = false,
  locale?: string,
): { time: string; offset: string } {
  const time = new Intl.DateTimeFormat(locale, {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12,
  }).format(now);

  const offset =
    new Intl.DateTimeFormat(locale, {
      timeZone,
      timeZoneName: "shortOffset",
    })
      .formatToParts(now)
      .find((part) => part.type === "timeZoneName")?.value ?? "";

  return { time, offset };
}

export function worldClockCityById(id: string): WorldClockCity | undefined {
  return TIMER_WORLD_CLOCK.find((city) => city.id === id);
}
